// src/components/ClientManagement.js

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import 'animate.css';
import 'animate.css';
import ConflictCheckModal from './ConflictCheckModal';
import { normalizeApiNextUrl } from '../utils/normalizeApiNextUrl';
import { formatCurrency } from '../utils/currency';
import SendPdfButton from './shared/SendPdfButton';
import DocumentExplorer from './DocumentExplorer';

function ClientManagement({
  clients,
  authAxios,
  fetchClients,
  showFormMessage,
  currentUser,
  genderOptions,
  riskLevelOptions,
  fetchComplianceItems, // --- NEW: Receive fetchComplianceItems prop
}) {
  // State for UI toggles
  const [showAddIndividualClientForm, setShowAddIndividualClientForm] = useState(false);
    const [showAddOrgClientForm, setShowAddOrgClientForm] = useState(false);
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [clientViewMode, setClientViewMode] = useState('tiles');

  // --- NEW: State for search, sort, and filter ---
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'first_name', direction: 'ascending' });

  // Restore saved view preferences from localStorage
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem('clients.ui') || '{}');
      if (p.viewMode) setClientViewMode(p.viewMode);
      if (typeof p.searchTerm === 'string') setSearchTerm(p.searchTerm);
      if (typeof p.typeFilter === 'string') setTypeFilter(p.typeFilter);
      if (typeof p.riskFilter === 'string') setRiskFilter(p.riskFilter);
      if (p.sortConfig && p.sortConfig.key) setSortConfig(p.sortConfig);
    } catch (_) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('clients.ui', JSON.stringify({ viewMode: clientViewMode, searchTerm, typeFilter, riskFilter, sortConfig })); } catch (_) {}
  }, [clientViewMode, searchTerm, typeFilter, riskFilter, sortConfig]);

  // State for client details modal and its data
  const [showClientDetailsModal, setShowClientDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientRecords, setClientRecords] = useState({
    cases: [],
    documents: [],
    timeEntries: [],
    expenseEntries: [],
    invoices: [],
    communicationLogs: [],
    tasks: [],
        debtRecoveryRequests: [],
  });
  const [isFetchingRecords, setIsFetchingRecords] = useState(false);
  // --- NEW: State for active tab in details modal ---
  const [activeDetailsTab, setActiveDetailsTab] = useState('cases');
    // Statement of Account (PDF)
    const [showStatementModal, setShowStatementModal] = useState(false);
    const [statementStartDate, setStatementStartDate] = useState('');
    const [statementEndDate, setStatementEndDate] = useState('');
    const [statementPreset, setStatementPreset] = useState('30');
    const [includeOutstanding, setIncludeOutstanding] = useState(true);
  // --- NEW: State for checklist dropdown toggle ---
  const [openChecklistDropdown, setOpenChecklistDropdown] = useState(null);
  const dropdownRef = useRef(null);


  // State for Conflict of Interest
  const [showConflictCheckModal, setShowConflictCheckModal] = useState(false);
  const [currentConflictCheckResult, setCurrentConflictCheckResult] = useState(null);
  const [pendingClientData, setPendingClientData] = useState(null);

  // State for Archive confirmation
  const [clientToArchive, setClientToArchive] = useState(null);
  const [archiveInProgress, setArchiveInProgress] = useState(false);


  // State for new client form data (Individual)
  const [newIndividualClient, setNewIndividualClient] = useState({
        first_name: '', last_name: '', email: '', phone_number: '',
        id_or_passport_number: '',
        id_type: '',
        country_of_issue: '',
        id_expiry_date: '',
        nationality: '',
        physical_residential_address: '',
        proof_of_address_document: null,
      consent_form_document: null,
        date_of_birth: '',
        gender: 'MALE', occupation: '', is_portal_active: false, user: '',
        risk_level: 'Low',
        risk_assessment_comments: '',
        parties_involved: ''
    });

  // State for new client form data (Organizational)
  const [newOrganizationalClient, setNewOrganizationalClient] = useState({
    first_name: '', 
    last_name: '', 
    email: '',
    organization_name: '',
    registration_number: '',
    date_of_incorporation: '',
    registered_address: '',
    physical_business_address: '',
    business_type: '',
    tax_identification_number: '',
    contact_person: '',
    phone_number: '',
    is_portal_active: false,
    user: '',
    risk_level: 'Low',
    risk_assessment_comments: '',
    beneficial_owners: [
        {
            first_name: '', last_name: '', email: '', phone_number: '',
            physical_residential_address: '', id_or_passport_number: '',
            id_type: '', country_of_issue: '', id_expiry_date: '',
            date_of_birth: '', nationality: '', is_director: false,
            is_shareholder: false, percentage_of_shares: '', ownership_type: ''
        }
    ],
    trust_deed_document: null,
    partnership_agreement_document: null,
        cipa_registration_document: null,
        tax_clearance_document: null,
        consent_form_document: null,
    parties_involved: ''
  });

  // States for editing client
  const [editingClient, setEditingClient] = useState(null);

    // --- NEW: Memoized, filtered, and sorted client list ---
    const filteredAndSortedClients = useMemo(() => {
        let filtered = [...clients];

        if (searchTerm) {
            filtered = filtered.filter(client => {
                const name = client.client_type === 'individual' ? `${client.first_name} ${client.last_name}` : client.organization_name;
                return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()));
            });
        }

        if (typeFilter) {
            filtered = filtered.filter(client => client.client_type === typeFilter);
        }

        if (riskFilter) {
            filtered = filtered.filter(client => client.risk_level === riskFilter);
        }

        filtered.sort((a, b) => {
            let aValue, bValue;
            
            if (sortConfig.key === 'name') {
                aValue = a.client_type === 'individual' ? `${a.first_name} ${a.last_name}`.toLowerCase() : a.organization_name.toLowerCase();
                bValue = b.client_type === 'individual' ? `${b.first_name} ${b.last_name}`.toLowerCase() : b.organization_name.toLowerCase();
            } else {
                aValue = a[sortConfig.key];
                bValue = b[sortConfig.key];
            }

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [clients, searchTerm, typeFilter, riskFilter, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

  // --- Helper Functions ---

        const triggerToast = useCallback((message, type = 'success') => {
            if (typeof showFormMessage === 'function') {
                showFormMessage(type, message);
                return;
            }
            // If global toast is not available, fall back to console so developer notices the message
            console.warn('Toast:', type, message);
        }, [showFormMessage]);
  
  // --- Close dropdown on outside click ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenChecklistDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Open the add-individual-client form when a Quick Action dispatches 'add_new'.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.tab === 'clients' && e.detail?.type === 'add_new') {
        setShowAddIndividualClientForm(true);
      }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, []);

  // --- Individual Client Form Handlers ---
  const handleIndividualFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        let nextValue;
        if (type === 'checkbox') {
            nextValue = checked;
        } else if (type === 'file') {
            nextValue = files[0];
        } else {
            nextValue = value;
        }
        setNewIndividualClient({
            ...newIndividualClient,
            [name]: nextValue,
        });
    };

  const handleIndividualFormSubmit = async (e) => {
        e.preventDefault();
        
        const partiesToSubmit = parsePartiesInvolved(newIndividualClient.parties_involved);
        if (partiesToSubmit.length > 0) {
            try {
                const conflictResponse = await authAxios.post('conflict-checks/', {
                    party_name_checked: partiesToSubmit.join(', '),
                    details: { parties_to_check: partiesToSubmit },
                    status: 'POTENTIAL'
                });

                if (conflictResponse.data.status === 'POTENTIAL') {
                    setCurrentConflictCheckResult(conflictResponse.data);
                    setPendingClientData({ clientData: newIndividualClient, isOrgClient: false, isEdit: false, clientId: null });
                    setShowConflictCheckModal(true);
                    showFormMessage('warning', 'Potential conflict of interest detected. Please review and resolve before proceeding.');
                    return; 
                }
            } catch (error) {
                console.error('Error during initial conflict check:', error.response ? error.response.data : error.message);
                showFormMessage('danger', 'Failed to perform initial conflict check. Please try again.');
                return;
            }
        }

        try {
            const formData = new FormData();
            for (const key in newIndividualClient) {
                if (key === 'parties_involved') { 
                    formData.append(key, JSON.stringify(partiesToSubmit));
                } else {
                    if (newIndividualClient[key] !== null && newIndividualClient[key] !== undefined && !(newIndividualClient[key] instanceof File)) {
                        formData.append(key, newIndividualClient[key]);
                    } else if (newIndividualClient[key] instanceof File) {
                        formData.append(key, newIndividualClient[key]);
                    }
                }
            }
            formData.append('client_type', 'individual');
            formData.append('created_by', currentUser.id);

            await authAxios.post('individual-clients/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            triggerToast('Individual client added successfully!', 'success');
            fetchClients();
            setShowAddIndividualClientForm(false);
            setNewIndividualClient({
              first_name: '', last_name: '', email: '', phone_number: '',
              id_or_passport_number: '', id_type: '', country_of_issue: '',
              id_expiry_date: '', nationality: '', physical_residential_address: '',
                            proof_of_address_document: null, consent_form_document: null, date_of_birth: '', gender: 'MALE',
              occupation: '', is_portal_active: false, user: '', risk_level: 'Low',
              risk_assessment_comments: '',
              parties_involved: ''
            });
        } catch (error) {
            // If server returned 409 Conflict with conflict details, open the conflict modal
            if (error && error.response && error.response.status === 409) {
                const conflicts = error.response.data && (error.response.data.conflicts || error.response.data.conflict_of_interest || []);
                const conflictObj = {
                    id: error.response.data && error.response.data.id ? error.response.data.id : null,
                    status: 'POTENTIAL',
                    details: { conflicts_found: conflicts },
                    party_name_checked: partiesToSubmit.join(', ')
                };
                setCurrentConflictCheckResult(conflictObj);
                setPendingClientData({ clientData: newIndividualClient, isOrgClient: false, isEdit: false, clientId: null });
                setShowConflictCheckModal(true);
                showFormMessage('warning', 'Potential conflict of interest detected. Please review and resolve.');
                return;
            }

            console.error('Error adding individual client:', error);
            triggerToast('Failed to add individual client. Please check the form.', 'danger');
        }
    };

  // --- Organizational Client Form Handlers ---
  const handleOrganizationalFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        let nextValue;
        if (type === 'checkbox') {
            nextValue = checked;
        } else if (type === 'file') {
            nextValue = files[0];
        } else {
            nextValue = value;
        }
        setNewOrganizationalClient({
            ...newOrganizationalClient,
            [name]: nextValue,
        });
    };

  const validateBeneficialOwners = (beneficialOwners) => {
    let isValid = true;
    const requiredUboFields = [
      'first_name', 'last_name', 'physical_residential_address',
      'id_or_passport_number', 'id_type', 'country_of_issue',
      'date_of_birth', 'nationality'
    ];

    beneficialOwners.forEach((ubo, index) => {
      requiredUboFields.forEach(field => {
        if (!ubo[field] || String(ubo[field]).trim() === '') {
          isValid = false;
          console.error(`UBO ${index + 1}: Field \'${field}' is required.`);
          triggerToast(`Beneficial Owner ${index + 1}: Please fill in all required fields (marked with *).`, 'danger');
        }
      });
    });
    return isValid;
  };


  const handleOrganizationalFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateBeneficialOwners(newOrganizationalClient.beneficial_owners)) {
      return; 
    }

    const partiesToSubmit = parsePartiesInvolved(newOrganizationalClient.parties_involved);
    if (partiesToSubmit.length > 0) {
        try {
            const conflictResponse = await authAxios.post('conflict-checks/', {
                party_name_checked: partiesToSubmit.join(', '),
                details: { parties_to_check: partiesToSubmit },
                status: 'POTENTIAL'
            });

            if (conflictResponse.data.status === 'POTENTIAL') {
                setCurrentConflictCheckResult(conflictResponse.data);
                setPendingClientData({ clientData: newOrganizationalClient, isOrgClient: true, isEdit: false, clientId: null });
                setShowConflictCheckModal(true);
                showFormMessage('warning', 'Potential conflict of interest detected. Please review and resolve before proceeding.');
                return;
            }
        } catch (error) {
            console.error('Error during initial conflict check:', error.response ? error.response.data : error.message);
            showFormMessage('danger', 'Failed to perform initial conflict check. Please try again.');
            return;
        }
    }

    const formData = new FormData();

    for (const key in newOrganizationalClient) {
        if (key === 'beneficial_owners') {
            const formattedBeneficialOwners = newOrganizationalClient[key].map(ubo => ({
                ...ubo,
                date_of_birth: ubo.date_of_birth ? new Date(ubo.date_of_birth).toISOString().split('T')[0] : null,
                id_expiry_date: ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toISOString().split('T')[0] : null,
            }));
            formData.append('beneficial_owners_json', JSON.stringify(formattedBeneficialOwners));
        } else if (key === 'parties_involved') {
            formData.append(key, JSON.stringify(partiesToSubmit));
        } else if (newOrganizationalClient[key] instanceof File) {
            formData.append(key, newOrganizationalClient[key]);
        }
        else if (newOrganizationalClient[key] !== null && newOrganizationalClient[key] !== undefined) {
            formData.append(key, newOrganizationalClient[key]);
        }
    }
    formData.append('client_type', 'organizational');
    formData.append('created_by', currentUser.id);

    if (formData.get('date_of_incorporation')) {
        formData.set('date_of_incorporation', new Date(formData.get('date_of_incorporation')).toISOString().split('T')[0]);
    }

    try {
        await authAxios.post('organizational-clients/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        showFormMessage('success', 'Organizational client added successfully!');
        fetchClients();
        setShowAddOrgClientForm(false);
        setNewOrganizationalClient({
            first_name: '', last_name: '', email: '', organization_name: '', phone_number: '',
            registration_number: '', date_of_incorporation: '', registered_address: '',
            physical_business_address: '', business_type: '', tax_identification_number: '', contact_person: '',
            is_portal_active: false, user: '', risk_level: 'Low', risk_assessment_comments: '',
            beneficial_owners: [{
                first_name: '', last_name: '', email: '', phone_number: '',
                physical_residential_address: '', id_or_passport_number: '',
                id_type: '', country_of_issue: '', id_expiry_date: '',
                date_of_birth: '', nationality: '', is_director: false,
                is_shareholder: false, percentage_of_shares: '', ownership_type: ''
            }],
            trust_deed_document: null,
            partnership_agreement_document: null,
            cipa_registration_document: null,
            tax_clearance_document: null,
            consent_form_document: null,
            parties_involved: ''
        });
    } catch (error) {
                // If server returned 409 Conflict with conflict details, open the conflict modal
                if (error && error.response && error.response.status === 409) {
                        const conflicts = error.response.data && (error.response.data.conflicts || error.response.data.conflict_of_interest || []);
                        const conflictObj = {
                                id: error.response.data && error.response.data.id ? error.response.data.id : null,
                                status: 'POTENTIAL',
                                details: { conflicts_found: conflicts },
                                party_name_checked: partiesToSubmit.join(', ')
                        };
                        setCurrentConflictCheckResult(conflictObj);
                        setPendingClientData({ clientData: newOrganizationalClient, isOrgClient: true, isEdit: false, clientId: null });
                        setShowConflictCheckModal(true);
                        showFormMessage('warning', 'Potential conflict of interest detected. Please review and resolve.');
                        return;
                }

                console.error('Error adding organizational client:', error.response ? error.response.data : error);
                let errorMessage = 'Failed to add organizational client. Please check the form.';
                if (error.response && error.response.data) {
                    const errors = Object.values(error.response.data).flat();
                    if (errors.length > 0) {
                        errorMessage = errors.join(' ');
                    }
                }
                showFormMessage('danger', errorMessage);
    }
  };

  const handleUboChange = (index, e, clientState, setClientState) => {
      const { name, value, type, checked } = e.target;
      const currentBeneficialOwners = Array.isArray(clientState.beneficial_owners) ? [...clientState.beneficial_owners] : [];
      if (currentBeneficialOwners[index]) { 
          currentBeneficialOwners[index] = {
              ...currentBeneficialOwners[index],
              [name]: type === 'checkbox' ? checked : value
          };
      }
      setClientState({ ...clientState, beneficial_owners: currentBeneficialOwners });
  };

  const handleAddUbo = (clientState, setClientState) => {
      const currentBeneficialOwners = Array.isArray(clientState.beneficial_owners) ? [...clientState.beneficial_owners] : [];
      setClientState({
          ...clientState,
          beneficial_owners: [
              ...currentBeneficialOwners,
              {
                  first_name: '', last_name: '', email: '', phone_number: '',
                  physical_residential_address: '', id_or_passport_number: '',
                  id_type: '', country_of_issue: '', id_expiry_date: '',
                  date_of_birth: '', nationality: '', is_director: false,
                  is_shareholder: false, percentage_of_shares: '', ownership_type: ''
              }
          ],
      });
  };

  const handleRemoveUbo = (index, clientState, setClientState) => {
      const currentBeneficialOwners = Array.isArray(clientState.beneficial_owners) ? [...clientState.beneficial_owners] : [];
      currentBeneficialOwners.splice(index, 1);
      setClientState({ ...clientState, beneficial_owners: currentBeneficialOwners });
  };


  // --- Edit Client Modal Handlers ---
  const openEditClientModal = (client) => {
    const clientToEdit = JSON.parse(JSON.stringify(client));

    if (clientToEdit.client_type === 'individual') {
        if (clientToEdit.date_of_birth) clientToEdit.date_of_birth = new Date(clientToEdit.date_of_birth).toISOString().split('T')[0];
        if (clientToEdit.id_expiry_date) clientToEdit.id_expiry_date = clientToEdit.id_expiry_date ? new Date(clientToEdit.id_expiry_date).toISOString().split('T')[0] : '';
    } else if (clientToEdit.client_type === 'organizational') {
        if (clientToEdit.date_of_incorporation) clientToEdit.date_of_incorporation = new Date(clientToEdit.date_of_incorporation).toISOString().split('T')[0];
        if (clientToEdit.beneficial_owners) {
            clientToEdit.beneficial_owners = clientToEdit.beneficial_owners.map(ubo => ({
                ...ubo,
                date_of_birth: ubo.date_of_birth ? new Date(ubo.date_of_birth).toISOString().split('T')[0] : '',
                id_expiry_date: ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toISOString().split('T')[0] : '',
            }));
        }
    }
    clientToEdit.parties_involved = Array.isArray(clientToEdit.parties_involved) ? clientToEdit.parties_involved.join(', ') : (clientToEdit.parties_involved || '');

    setEditingClient(clientToEdit);
    setShowEditClientModal(true);
    setShowClientDetailsModal(false);
  };

  const closeEditClientModal = () => {
    setEditingClient(null);
    setShowEditClientModal(false);
    setPendingClientData(null);
  };

  const handleEditClientChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setEditingClient(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'file' ? files[0] : value)
    }));
  };

  // --- Archive Client Handlers ---
  const openArchiveConfirm = (client) => {
    setClientToArchive(client);
  };

  const closeArchiveConfirm = () => {
    if (archiveInProgress) return;
    setClientToArchive(null);
  };

  const confirmArchiveClient = async () => {
    if (!clientToArchive) return;
    const endpoint = clientToArchive.client_type === 'individual'
      ? `individual-clients/${clientToArchive.id}/archive/`
      : `organizational-clients/${clientToArchive.id}/archive/`;
    setArchiveInProgress(true);
    try {
      await authAxios.post(endpoint);
      triggerToast('Client archived.', 'success');
      setClientToArchive(null);
      fetchClients();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to archive client.';
      triggerToast(detail, 'danger');
    } finally {
      setArchiveInProgress(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();

    const partiesToSubmit = parsePartiesInvolved(editingClient.parties_involved);
    if (partiesToSubmit.length > 0) {
        try {
            const conflictResponse = await authAxios.post(`clients/${editingClient.id}/check-conflict/`, {
                parties_to_check: partiesToSubmit,
            });

            if (conflictResponse.data.status === 'POTENTIAL') {
                setCurrentConflictCheckResult(conflictResponse.data);
                setPendingClientData({ clientData: editingClient, isOrgClient: editingClient.client_type === 'organizational', isEdit: true, clientId: editingClient.id });
                setShowConflictCheckModal(true);
                showFormMessage('warning', 'Potential conflict of interest detected. Please review and resolve before proceeding.');
                return;
            }
        } catch (error) {
            console.error('Error during update conflict check:', error.response ? error.response.data : error.message);
            showFormMessage('danger', 'Failed to perform conflict check for update. Please try again.');
            return;
        }
    }

    try {
      const endpoint = editingClient.client_type === 'individual'
        ? `individual-clients/${editingClient.id}/` 
        : `organizational-clients/${editingClient.id}/`;

      // Create a mutable copy of the client data to modify before sending
      const payload = { ...editingClient };
      
      // Requirement: Don't update the checklist on edit. Also fixes FormData/JSON issue.
      delete payload.onboarding_checklist;

      // Format dates correctly before sending
      if (payload.client_type === 'individual') {
          if (payload.date_of_birth) payload.date_of_birth = new Date(payload.date_of_birth).toISOString().split('T')[0];
          if (payload.id_expiry_date) payload.id_expiry_date = payload.id_expiry_date ? new Date(payload.id_expiry_date).toISOString().split('T')[0] : null;
      } else if (payload.client_type === 'organizational') {
          if (payload.date_of_incorporation) payload.date_of_incorporation = new Date(payload.date_of_incorporation).toISOString().split('T')[0];
          if (payload.beneficial_owners) {
              payload.beneficial_owners = payload.beneficial_owners.map(ubo => ({
                  ...ubo,
                  date_of_birth: ubo.date_of_birth ? new Date(ubo.date_of_birth).toISOString().split('T')[0] : '',
                  id_expiry_date: ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toISOString().split('T')[0] : '',
              }));
          }
      }

            const fileKeys = [
                'proof_of_address_document',
                'consent_form_document',
                'trust_deed_document',
                'partnership_agreement_document',
                'cipa_registration_document',
                'tax_clearance_document',
            ];
      let hasFileChanges = false;

      // Check for new file uploads and clean the payload of old file URLs
      fileKeys.forEach(key => {
        if (payload[key] && payload[key] instanceof File) {
            hasFileChanges = true;
        } else {
            // If it's not a new file, it's either null or a URL string. 
            // We don't want to send the URL string.
            delete payload[key];
        }
      });
      
      let response;
      if (hasFileChanges || editingClient.client_type === 'organizational') {
          const formData = new FormData();
          for (const key in payload) {
            if (key === 'beneficial_owners') {
              const formattedBeneficialOwners = payload[key].map(ubo => ({
                  ...ubo,
                  date_of_birth: ubo.date_of_birth ? new Date(ubo.date_of_birth).toISOString().split('T')[0] : null,
                  id_expiry_date: ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toISOString().split('T')[0] : null,
              }));
              formData.append('beneficial_owners_json', JSON.stringify(formattedBeneficialOwners));
            } else if (key === 'parties_involved') {
                formData.append(key, JSON.stringify(partiesToSubmit));
            } else if (payload[key] !== null && payload[key] !== undefined) {
                formData.append(key, payload[key]);
            }
          }
          response = await authAxios.patch(endpoint, formData, {
              headers: {
                  'Content-Type': 'multipart/form-data',
              },
          });
      } else {
          // No files changed and it's an individual client, so send as JSON.
          // The payload is already clean of file URLs.
          response = await authAxios.patch(endpoint, payload, {
              headers: {
                  'Content-Type': 'application/json',
              },
          });
      }

      triggerToast('Client updated successfully!', 'success');
      fetchClients();
      closeEditClientModal();
      setPendingClientData(null);
    } catch (error) {
      console.error('Error updating client:', error.response ? error.response.data : error);
      let errorMessage = 'Failed to update client. Please try again.';
      if (error.response && error.response.data) {
          const errors = Object.values(error.response.data).flat();
          if (errors.length > 0) {
            errorMessage = errors.join(' ');
          }
        }
      triggerToast(errorMessage, 'danger');
      setPendingClientData(null);
    }
  };


  // --- Client Details Modal Handlers and Fetching ---

  const fetchAllPages = useCallback(async (url) => {
    let results = [];
    let nextUrl = url;
    while (nextUrl) {
        try {
            const response = await authAxios.get(normalizeApiNextUrl(nextUrl));
            const data = response.data;
            if (data && Array.isArray(data.results)) {
                results = results.concat(data.results);
                nextUrl = normalizeApiNextUrl(data.next); // Get the URL for the next page
            } else if (Array.isArray(data)) {
                // Handle non-paginated responses
                results = results.concat(data);
                nextUrl = null;
            } else {
                // No more results or unexpected format
                nextUrl = null;
            }
        } catch (error) {
            console.error(`Error fetching data from ${nextUrl}:`, error);
            triggerToast(`Failed to fetch some records for ${url}. The list may be incomplete.`, 'danger');
            break; // Exit loop on error
        }
    }
    return results;
}, [authAxios, triggerToast]);

    const fetchClientRecords = useCallback(async (clientId) => {
      setIsFetchingRecords(true);
      setClientRecords({
        cases: [],
        documents: [],
        timeEntries: [],
        expenseEntries: [],
        invoices: [],
        communicationLogs: [],
        tasks: [],
                debtRecoveryRequests: [],
      });
      try {
        // Fetch standard records and also the client's cases and direct tasks
        const [
          documents,
          timeEntries,
          expenseEntries,
          invoices,
          communicationLogs,
          cases,
                    tasks,
                    debtRecoveryRequests,
        ] = await Promise.all([
          fetchAllPages(`documents/?client=${clientId}`),
          fetchAllPages(`time-entries/?client=${clientId}`),
          fetchAllPages(`expense-entries/?client=${clientId}`),
          fetchAllPages(`invoices/?client=${clientId}`),
          fetchAllPages(`communication-logs/?client=${clientId}`),
          fetchAllPages(`cases/?client=${clientId}`),
          // Fetch all tasks related to the client in one go.
          // The backend should handle filtering for tasks linked directly to the client
          // OR to any of the client's cases.
          fetchAllPages(`tasks/?client=${clientId}`),
                    fetchAllPages(`debt-recovery-requests/?requesting_client=${clientId}`),
        ]);
  
        setClientRecords({
          cases,
          documents,
          timeEntries,
          expenseEntries,
          invoices,
          communicationLogs,
          tasks,
                    debtRecoveryRequests,
        });
      } catch (error) {
        console.error("Error fetching all client records:", error);
        triggerToast('Failed to fetch all client records. The data may be incomplete.', 'danger');
      } finally {
        setIsFetchingRecords(false);
      }
    }, [triggerToast, fetchAllPages]);

  const openClientDetailsModal = useCallback((client) => {
    setSelectedClient(client);
    setActiveDetailsTab('cases'); // Reset to the first tab
    console.log("Selected Client Data for Details Modal:", client);
    if (client.client_type === 'organizational') {
        console.log("Beneficial Owners in Selected Client:", client.beneficial_owners);
    }
    fetchClientRecords(client.id);
    setShowClientDetailsModal(true);
  }, [fetchClientRecords]);

  const closeClientDetailsModal = useCallback(() => {
    setSelectedClient(null);
    setClientRecords({
      cases: [],
      documents: [],
      timeEntries: [],
      expenseEntries: [],
      invoices: [],
      communicationLogs: [],
      tasks: [],
            debtRecoveryRequests: [],
    });
    setShowClientDetailsModal(false);
  }, []);

    const openStatementModal = useCallback(() => {
        const today = new Date();
        const end = today.toISOString().split('T')[0];
        const start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        setStatementPreset('30');
        setStatementStartDate(start);
        setStatementEndDate(end);
        setShowStatementModal(true);
    }, []);

    const applyStatementPreset = useCallback((preset) => {
        setStatementPreset(preset);
        const today = new Date();
        const end = today.toISOString().split('T')[0];
        let start = '';
        if (preset === '30') {
            start = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        } else if (preset === '90') {
            start = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        } else if (preset === '365') {
            start = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        } else if (preset === 'all') {
            start = '';
        }
        setStatementStartDate(start);
        setStatementEndDate(end);
    }, []);

    // Fetch the branded client statement PDF using whatever date/filter the
    // user has set in the statement modal. Used both by the Download button
    // and by SendPdfButton's `fetchPdf` thunk.
    const fetchClientStatementPdf = useCallback(async () => {
        if (!selectedClient) throw new Error('No client selected.');
        const params = {};
        if (statementStartDate) params.start_date = statementStartDate;
        if (statementEndDate) params.end_date = statementEndDate;
        params.include_outstanding = includeOutstanding ? 'true' : 'false';
        const response = await authAxios.get(`clients/${selectedClient.id}/statement/`, {
            params,
            responseType: 'blob',
        });
        return new Blob([response.data], { type: 'application/pdf' });
    }, [authAxios, selectedClient, statementStartDate, statementEndDate, includeOutstanding]);

    // Adapter so SendPdfButton (expects showFormMessage(type, msg)) can talk
    // to ClientManagement's existing triggerToast(msg, type).
    const sendPdfToast = useCallback((type, msg) => {
        triggerToast(msg, type === 'success' ? 'success' : 'danger');
    }, [triggerToast]);

    const handleDownloadStatement = useCallback(async () => {
        if (!selectedClient) return;
        try {
            const blob = await fetchClientStatementPdf();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            const startLabel = statementStartDate ? statementStartDate : 'all';
            const endLabel = statementEndDate ? statementEndDate : 'today';
            a.download = `statement-client-${selectedClient.id}-${startLabel}-to-${endLabel}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            triggerToast('Statement downloaded', 'success');
            setShowStatementModal(false);
        } catch (error) {
            console.error('Error downloading statement:', error.response ? error.response.data : error);
            triggerToast('Failed to download statement', 'danger');
        }
    }, [selectedClient, statementStartDate, statementEndDate, fetchClientStatementPdf, triggerToast]);

  const parsePartiesInvolved = (partiesString) => {
    if (!partiesString) return [];
    return partiesString.split(',').map(name => name.trim()).filter(name => name !== '');
  };

  const handleConflictResolved = useCallback((updatedConflictCheck) => {
    setCurrentConflictCheckResult(updatedConflictCheck);
    if (updatedConflictCheck.status === 'WAIVED') {
      if (pendingClientData) {
        const { clientData, isOrgClient, isEdit, clientId } = pendingClientData;
        
        const partiesToResubmit = parsePartiesInvolved(clientData.parties_involved);

        const formData = new FormData();
                const fileKeys = [
                    'proof_of_address_document',
                    'consent_form_document',
                    'trust_deed_document',
                    'partnership_agreement_document',
                    'cipa_registration_document',
                    'tax_clearance_document',
                ];

        for (const key in clientData) {
            if (fileKeys.includes(key)) {
                if (clientData[key] instanceof File) {
                    formData.append(key, clientData[key]);
                }
            } else if (key === 'beneficial_owners' && isOrgClient) {
                const formattedBeneficialOwners = clientData[key].map(ubo => ({
                    ...ubo,
                    date_of_birth: ubo.date_of_birth ? new Date(ubo.date_of_birth).toISOString().split('T')[0] : null,
                    id_expiry_date: ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toISOString().split('T')[0] : null,
                }));
                formData.append('beneficial_owners_json', JSON.stringify(formattedBeneficialOwners));
            } else if (key === 'parties_involved') {
                formData.append(key, JSON.stringify(partiesToResubmit));
            }
            else if (clientData[key] !== null && clientData[key] !== undefined) {
                formData.append(key, clientData[key]);
            }
        }
        if (!isEdit) { 
            formData.append('client_type', isOrgClient ? 'organizational' : 'individual');
            formData.append('created_by', currentUser.id);
        }

        const submitApiCall = async () => {
          try {
            if (isEdit) {
              const endpoint = isOrgClient ? `organizational-clients/${clientId}/` : `individual-clients/${clientId}/`;
              await authAxios.patch(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
              showFormMessage('success', 'Client updated successfully after conflict waiver!');
              setShowEditClientModal(false);
            } else {
              const createEndpoint = isOrgClient ? 'organizational-clients/' : 'individual-clients/';
              // Include force flag so backend pre-create checks allow proceeding after a waiver
              formData.append('force', 'true');
              await authAxios.post(createEndpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
              showFormMessage('success', 'Client added successfully after conflict waiver!');
              if (isOrgClient) setShowAddOrgClientForm(false); 
              else setShowAddIndividualClientForm(false);
            }
            fetchClients();
          } catch (error) {
            console.error('Error submitting client after waiver:', error.response ? error.response.data : error.message);
            showFormMessage('danger', 'Failed to submit client after waiver.');
          } finally {
            setPendingClientData(null);
            setShowConflictCheckModal(false);
          }
        };
        submitApiCall();

      }
    } else if (updatedConflictCheck.status === 'DECLINED') {
      showFormMessage('info', 'Engagement declined due to conflict of interest.');
      setShowConflictCheckModal(false);
      setPendingClientData(null);
      if (showAddIndividualClientForm) setShowAddIndividualClientForm(false);
      if (showAddOrgClientForm) setShowAddOrgClientForm(false);
      if (showEditClientModal) setShowEditClientModal(false);
    }
  }, [pendingClientData, showFormMessage, authAxios, fetchClients, currentUser,
      showAddIndividualClientForm, showAddOrgClientForm, showEditClientModal]);

  const handleUpdateOnboardingStatus = async (itemKey, status) => {
    if (!selectedClient) return;
    try {
        const response = await authAxios.post(`clients/${selectedClient.id}/update-onboarding-status/`, {
            item_key: itemKey,
            status: status
        });
        
        setSelectedClient(response.data);
        fetchClients();
        if (fetchComplianceItems) fetchComplianceItems(); // --- NEW: Refresh compliance items

        triggerToast(`Onboarding status for '${formatChecklistItemName(itemKey)}' updated to '${status}'.`, 'success');
        setOpenChecklistDropdown(null); // Close dropdown after selection
    } catch (error) {
        console.error('Error updating onboarding status:', error);
        triggerToast('Failed to update onboarding status.', 'danger');
    }
  };

  const formatChecklistItemName = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusColor = (status) => {
    switch (status) {
        case 'completed': return 'green';
        case 'in_progress': return 'blue';
        case 'skipped': return 'gray';
        case 'pending':
        default: return 'yellow';
    }
  };


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Client Management</h2>

      {/* Add Client Buttons & View Toggles */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
            onClick={() => setShowAddIndividualClientForm(!showAddIndividualClientForm)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 11a1 1 0 100-2h-1v-1a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1z" />
            </svg>
            {showAddIndividualClientForm ? 'Cancel' : 'Add Individual Client'}
          </button>
          <button
            className="bg-teal-500 text-white px-4 py-2 rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 flex items-center"
            onClick={() => setShowAddOrgClientForm(!showAddOrgClientForm)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2H5a1 1 0 110-2V4zm3 1a1 1 0 011-1h2a1 1 0 011 1v1a1 1 0 01-1 1H8a1 1 0 01-1-1V5z" clipRule="evenodd" />
            </svg>
            {showAddOrgClientForm ? 'Cancel' : 'Add Organizational Client'}
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-sm font-medium ${clientViewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setClientViewMode('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`px-4 py-2 rounded-md text-sm font-medium ${clientViewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`}
            onClick={() => setClientViewMode('tiles')}
          >
            Tiles
          </button>
        </div>
      </div>


      {/* Add Individual Client Form Side Panel */}
      {showAddIndividualClientForm && (
          <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddIndividualClientForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddIndividualClientForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                      <h5 className="text-2xl font-bold text-gray-800">Add New Individual Client</h5>
                      <button onClick={() => setShowAddIndividualClientForm(false)} className="text-gray-600 hover:text-gray-900">
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                      </button>
                  </div>
                  <form onSubmit={handleIndividualFormSubmit} className="h-full">
                      <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">First Name: *</label>
                              <input type="text" name="first_name" value={newIndividualClient.first_name} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Last Name: *</label>
                              <input type="text" name="last_name" value={newIndividualClient.last_name} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Email: *</label>
                              <input type="email" name="email" value={newIndividualClient.email} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Phone Number:</label>
                              <input type="text" name="phone_number" value={newIndividualClient.phone_number} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Date of Birth: *</label>
                              <input type="date" name="date_of_birth" value={newIndividualClient.date_of_birth} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Gender: *</label>
                              <select name="gender" value={newIndividualClient.gender} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required>
                                <option value="">Select Gender</option>
                                {genderOptions.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">ID/Passport Number: *</label>
                              <input type="text" name="id_or_passport_number" value={newIndividualClient.id_or_passport_number} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">ID Type: *</label>
                              <input type="text" name="id_type" value={newIndividualClient.id_type} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Country of Issue: *</label>
                              <input type="text" name="country_of_issue" value={newIndividualClient.country_of_issue} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">ID Expiry Date:</label>
                              <input type="date" name="id_expiry_date" value={newIndividualClient.id_expiry_date} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Nationality: *</label>
                              <input type="text" name="nationality" value={newIndividualClient.nationality} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700">Physical Residential Address: *</label>
                              <input type="text" name="physical_residential_address" value={newIndividualClient.physical_residential_address} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Proof of Address Document:</label>
                              <input type="file" name="proof_of_address_document" onChange={handleIndividualFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700">Consent Form (PDF or image):</label>
                                                            <input type="file" name="consent_form_document" onChange={handleIndividualFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                                        </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Occupation:</label>
                              <input type="text" name="occupation" value={newIndividualClient.occupation} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Risk Level:</label>
                              <select name="risk_level" value={newIndividualClient.risk_level} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                {riskLevelOptions.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700">Risk Assessment Comments:</label>
                              <textarea name="risk_assessment_comments" value={newIndividualClient.risk_assessment_comments} onChange={handleIndividualFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows="3" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated names for conflict check):</label>
                              <textarea
                                name="parties_involved"
                                value={newIndividualClient.parties_involved}
                                onChange={handleIndividualFormChange}
                                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                rows="2"
                                placeholder="e.g., Opposing Party Name, Related Entity, Witness"
                              ></textarea>
                              <p className="mt-2 text-sm text-gray-500">Separate multiple names with commas.</p>
                            </div>
                            <div className="md:col-span-2 flex items-center">
                              <input type="checkbox" name="is_portal_active" checked={newIndividualClient.is_portal_active} onChange={handleIndividualFormChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id="individualPortalActive" />
                              <label htmlFor="individualPortalActive" className="ml-2 block text-sm text-gray-900">Client Portal Active</label>
                            </div>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                          <button type="button" onClick={() => setShowAddIndividualClientForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Add Client</button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* Add Organizational Client Form Side Panel */}
      {showAddOrgClientForm && (
          <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showAddOrgClientForm ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
              <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showAddOrgClientForm ? 'translate-x-0' : 'translate-x-full'}`}>
                  <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                      <h5 className="text-2xl font-bold text-gray-800">Add New Organizational Client</h5>
                      <button onClick={() => setShowAddOrgClientForm(false)} className="text-gray-600 hover:text-gray-900">
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                      </button>
                  </div>
                  <form onSubmit={handleOrganizationalFormSubmit} className="h-full">
                      <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Organization Name: *</label>
                                <input type="text" name="organization_name" value={newOrganizationalClient.organization_name} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Contact Person(Full Name):</label>
                                <input type="text" name="contact_person" value={newOrganizationalClient.contact_person} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone Number:</label>
                                <input type="text" name="phone_number" value={newOrganizationalClient.phone_number} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email:</label>
                                <input type="email" name="email" value={newOrganizationalClient.email} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Registration Number: *</label>
                                <input type="text" name="registration_number" value={newOrganizationalClient.registration_number} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date of Incorporation: *</label>
                                <input type="date" name="date_of_incorporation" value={newOrganizationalClient.date_of_incorporation} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Registered Address: *</label>
                                <input type="text" name="registered_address" value={newOrganizationalClient.registered_address} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Physical Business Address: *</label>
                                <input type="text" name="physical_business_address" value={newOrganizationalClient.physical_business_address} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Business Type: *</label>
                                <input type="text" name="business_type" value={newOrganizationalClient.business_type} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Tax Identification Number (TIN):</label>
                                <input type="text" name="tax_identification_number" value={newOrganizationalClient.tax_identification_number} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Risk Level: *</label>
                                <select name="risk_level" value={newOrganizationalClient.risk_level} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required>
                                    {riskLevelOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Risk Assessment Comments:</label>
                                <textarea name="risk_assessment_comments" value={newOrganizationalClient.risk_assessment_comments} onChange={handleOrganizationalFormChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows="3" />
                            </div>

                            <div className="md:col-span-2 p-4 border rounded-md">
                                <h5 className="text-lg font-bold text-gray-800 mb-4">Beneficial Owners</h5>
                                {newOrganizationalClient.beneficial_owners.map((ubo, index) => (
                                    <div key={index} className="p-4 border rounded-md mb-4 space-y-4">
                                        <div className="flex justify-between items-center">
                                            <h6 className="font-semibold text-gray-700">Beneficial Owner {index + 1}</h6>
                                            <button type="button" onClick={() => handleRemoveUbo(index, newOrganizationalClient, setNewOrganizationalClient)} className="text-red-500 hover:text-red-700">Remove</button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <input type="text" name="first_name" value={ubo.first_name} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="First Name *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <input type="text" name="last_name" value={ubo.last_name} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Last Name *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <input type="email" name="email" value={ubo.email} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Email" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                            <input type="text" name="phone_number" value={ubo.phone_number} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Phone Number" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                            <div className="md:col-span-2">
                                                <input type="text" name="physical_residential_address" value={ubo.physical_residential_address} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Physical Residential Address *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            </div>
                                            <input type="text" name="id_or_passport_number" value={ubo.id_or_passport_number} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="ID/Passport Number *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <input type="text" name="id_type" value={ubo.id_type} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="ID Type *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <input type="text" name="country_of_issue" value={ubo.country_of_issue} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Country of Issue *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <input type="text" name="nationality" value={ubo.nationality} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Nationality *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">ID Expiry Date</label>
                                                <input type="date" name="id_expiry_date" value={ubo.id_expiry_date} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="ID Expiry Date" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Date of Birth *</label>
                                                <input type="date" name="date_of_birth" value={ubo.date_of_birth} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Date of Birth *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                            </div>
                                            <div className="flex items-center">
                                                <input type="checkbox" name="is_director" checked={ubo.is_director} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id={`uboDirector-${index}`} />
                                                <label htmlFor={`uboDirector-${index}`} className="ml-2 block text-sm text-gray-900">Is Director?</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input type="checkbox" name="is_shareholder" checked={ubo.is_shareholder} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id={`uboShareholder-${index}`} />
                                                <label htmlFor={`uboShareholder-${index}`} className="ml-2 block text-sm text-gray-900">Is Shareholder?</label>
                                            </div>
                                            {ubo.is_shareholder && (
                                                <input type="number" step="0.01" name="percentage_of_shares" value={ubo.percentage_of_shares} onChange={e => handleUboChange(index, e, newOrganizationalClient, setNewOrganizationalClient)} placeholder="Percentage of Shares" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                            )}
                                        </div>
                                    </div>
                                ))} 
                                <button type="button" onClick={() => handleAddUbo(newOrganizationalClient, setNewOrganizationalClient)} className="mt-4 bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                    + Add Another Beneficial Owner
                                </button>
                            </div>

                            <div className="md:col-span-2 p-4 border rounded-md">
                                <h5 className="text-lg font-bold text-gray-800 mb-4">Trust / Partnership Documents</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Trust Deed Document:</label>
                                        <input type="file" name="trust_deed_document" onChange={handleOrganizationalFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Partnership Agreement Document:</label>
                                        <input type="file" name="partnership_agreement_document" onChange={handleOrganizationalFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-2 p-4 border rounded-md">
                                <h5 className="text-lg font-bold text-gray-800 mb-4">Regulatory / Key Documents</h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">CIPA Registration (PDF or image):</label>
                                        <input type="file" name="cipa_registration_document" onChange={handleOrganizationalFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Tax Clearance (PDF or image):</label>
                                        <input type="file" name="tax_clearance_document" onChange={handleOrganizationalFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Consent Form (PDF or image):</label>
                                        <input type="file" name="consent_form_document" onChange={handleOrganizationalFormChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated names for conflict check):</label>
                                <textarea
                                    name="parties_involved"
                                    value={newOrganizationalClient.parties_involved}
                                    onChange={handleOrganizationalFormChange}
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    rows="2"
                                    placeholder="e.g., Opposing Party Name, Related Entity, Witness"
                                ></textarea>
                                <p className="mt-2 text-sm text-gray-500">Separate multiple names with commas.</p>
                            </div>
                            
                            <div className="md:col-span-2 flex items-center">
                                <input type="checkbox" name="is_portal_active" checked={newOrganizationalClient.is_portal_active} onChange={handleOrganizationalFormChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id="orgPortalActive" />
                                <label htmlFor="orgPortalActive" className="ml-2 block text-sm text-gray-900">Client Portal Active</label>
                            </div>
                          </div>
                      </div>
                      <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                          <button type="button" onClick={() => setShowAddOrgClientForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Add Client</button>
                      </div>
                  </form>
              </div>
          </div>
      )}


      {/* Client List/Table */}
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
                <h5 className="text-lg font-bold text-gray-800">All Clients</h5>
            </div>
            <div className="p-6">
                {/* --- NEW: Search and Filter Controls --- */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <input
                        type="text"
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="">All Types</option>
                        <option value="individual">Individual</option>
                        <option value="organizational">Organizational</option>
                    </select>
                    <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}>
                        <option value="">All Risk Levels</option>
                        {riskLevelOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <button className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={() => { setSearchTerm(''); setTypeFilter(''); setRiskFilter(''); }}>
                        Clear
                    </button>
                </div>

                {clientViewMode === 'list' ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th onClick={() => requestSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Name/Organization {getSortIndicator('name')}</th>
                            <th onClick={() => requestSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Email {getSortIndicator('email')}</th>
                            <th onClick={() => requestSort('phone_number')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Phone {getSortIndicator('phone_number')}</th>
                            <th onClick={() => requestSort('client_type')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Type {getSortIndicator('client_type')}</th>
                            <th onClick={() => requestSort('risk_level')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Risk Level {getSortIndicator('risk_level')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAndSortedClients.length > 0 ? (
                        filteredAndSortedClients.map(client => (
                            <tr key={client.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{client.client_type === 'individual' ? `${client.first_name} ${client.last_name}` : client.organization_name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{client.phone_number}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.client_type === 'individual' ? 'bg-blue-100 text-blue-800' : 'bg-teal-100 text-teal-800'}`}>{client.client_type}</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.risk_level === 'High' ? 'bg-red-100 text-red-800' : client.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{client.risk_level}</span></td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-4">
                                <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditClientModal(client)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                    Edit
                                </button>
                                <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openClientDetailsModal(client)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                    View
                                </button>
                                <button className="text-amber-600 hover:text-amber-900 flex items-center" onClick={() => openArchiveConfirm(client)} title="Archive this client">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" /><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
                                    Archive
                                </button>
                            </td>
                            </tr>
                        ))
                        ) : (
                        <tr>
                            <td colSpan="6" className="text-center py-4 text-sm text-gray-500">No clients found.</td>
                        </tr>
                        )}
                    </tbody>
                    </table>
                </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredAndSortedClients.length > 0 ? (
                    filteredAndSortedClients.map(client => (
                        <div className="bg-white shadow-md rounded-lg overflow-hidden" key={client.id}>
                        <div className="p-6">
                            <h5 className="text-lg font-bold text-gray-800">
                                {client.client_type === 'individual' ? `${client.first_name} ${client.last_name}` : client.organization_name}
                            </h5>
                            <div className="flex space-x-2 mt-2">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.client_type === 'individual' ? 'bg-blue-100 text-blue-800' : 'bg-teal-100 text-teal-800'}`}>{client.client_type} Client</span>
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.risk_level === 'High' ? 'bg-red-100 text-red-800' : client.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{client.risk_level} Risk</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-4"><i className="far fa-envelope mr-2"></i>{client.email}</p>
                            <p className="text-sm text-gray-600"><i className="far fa-phone mr-2"></i>{client.phone_number}</p>
                            {client.client_type === 'individual' && (
                                <>
                                <p className="text-sm text-gray-600"><i className="far fa-id-card mr-2"></i>{client.id_or_passport_number || 'N/A'}</p>
                                <p className="text-sm text-gray-600"><i className="far fa-calendar-alt mr-2"></i>{client.date_of_birth ? new Date(client.date_of_birth).toLocaleDateString() : 'N/A'}</p>
                                <p className="text-sm text-gray-600"><i className="far fa-map-marker-alt mr-2"></i>{client.physical_residential_address || 'N/A'}</p>
                                </>
                            )}
                            {client.client_type === 'organizational' && (
                                <>
                                <p className="text-sm text-gray-600"><i className="far fa-user mr-2"></i>{client.contact_person}</p>
                                <p className="text-sm text-gray-600"><i className="far fa-building mr-2"></i>{client.registration_number || 'N/A'}</p>
                                <p className="text-sm text-gray-600"><i className="far fa-calendar-alt mr-2"></i>{client.date_of_incorporation ? new Date(client.date_of_incorporation).toLocaleDateString() : 'N/A'}</p>
                                </>
                            )}
                            <div className="mt-6 flex justify-end space-x-4">
                                <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditClientModal(client)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                    Edit
                                </button>
                                <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openClientDetailsModal(client)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                    View
                                </button>
                                <button className="text-amber-600 hover:text-amber-900 flex items-center" onClick={() => openArchiveConfirm(client)} title="Archive this client">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" /><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" /></svg>
                                    Archive
                                </button>
                            </div>
                        </div>
                        </div>
                    ))
                    ) : (
                    <div className="col-span-full text-center py-4 text-sm text-gray-500">No clients found.</div>
                    )}
                </div>
                )}
            </div>
        </div>

      {/* Edit Client Modal - MODIFIED to be a side panel */}
        {showEditClientModal && editingClient && (
            <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showEditClientModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showEditClientModal ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                        <h5 className="text-2xl font-bold text-gray-800">Edit Client: {editingClient.client_type === 'individual' ? `${editingClient.first_name} ${editingClient.last_name}` : editingClient.organization_name}</h5>
                        <button onClick={closeEditClientModal} className="text-gray-600 hover:text-gray-900">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <form onSubmit={handleUpdateClient} className="h-full">
                        <div className="p-6 space-y-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Common Fields for both client types */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email:</label>
                                    <input type="email" name="email" value={editingClient.email || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone Number:</label>
                                    <input type="text" name="phone_number" value={editingClient.phone_number || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Risk Level:</label>
                                    <select name="risk_level" value={editingClient.risk_level || 'low'} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                        {riskLevelOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Risk Assessment Comments:</label>
                                    <textarea name="risk_assessment_comments" value={editingClient.risk_assessment_comments || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows="3"></textarea>
                                </div>
                                <div className="flex items-center">
                                    <input type="checkbox" name="is_portal_active" checked={editingClient.is_portal_active} onChange={handleEditClientChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id="editPortalActive" />
                                    <label htmlFor="editPortalActive" className="ml-2 block text-sm text-gray-900">Client Portal Active</label>
                                </div>

                                {editingClient.client_type === 'individual' && (
                                <>
                                    <h6 className="text-lg font-bold text-gray-800 mt-6 mb-4 md:col-span-2">Individual Client Specifics</h6>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">First Name:</label>
                                        <input type="text" name="first_name" value={editingClient.first_name || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Last Name:</label>
                                        <input type="text" name="last_name" value={editingClient.last_name || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Date of Birth:</label>
                                        <input type="date" name="date_of_birth" value={editingClient.date_of_birth || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Gender:</label>
                                        <select name="gender" value={editingClient.gender || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                            {genderOptions.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">ID/Passport Number:</label>
                                        <input type="text" name="id_or_passport_number" value={editingClient.id_or_passport_number || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">ID Type:</label>
                                        <input type="text" name="id_type" value={editingClient.id_type || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Country of Issue:</label>
                                        <input type="text" name="country_of_issue" value={editingClient.country_of_issue || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">ID Expiry Date:</label>
                                        <input type="date" name="id_expiry_date" value={editingClient.id_expiry_date || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Nationality:</label>
                                        <input type="text" name="nationality" value={editingClient.nationality || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Physical Residential Address:</label>
                                        <input type="text" name="physical_residential_address" value={editingClient.physical_residential_address || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Proof of Address Document (current):</label>
                                        {editingClient.proof_of_address_document ? (
                                            <a href={editingClient.proof_of_address_document} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                        ) : (
                                            <span className="text-gray-500">No document uploaded</span>
                                        )}
                                        <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Proof of Address:</label>
                                        <input type="file" name="proof_of_address_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Consent Form (current):</label>
                                        {editingClient.consent_form_document_url ? (
                                            <a href={editingClient.consent_form_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                        ) : (
                                            <span className="text-gray-500">No document uploaded</span>
                                        )}
                                        <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Consent Form:</label>
                                        <input type="file" name="consent_form_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Occupation:</label>
                                        <input type="text" name="occupation" value={editingClient.occupation || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated names for conflict check):</label>
                                        <textarea
                                            name="parties_involved"
                                            value={editingClient.parties_involved || ''}
                                            onChange={handleEditClientChange}
                                            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            rows="2"
                                            placeholder="e.g., Opposing Party Name, Related Entity, Witness"
                                        ></textarea>
                                        <p className="mt-2 text-sm text-gray-500">Separate multiple names with commas.</p>
                                    </div>
                                </>
                                )}
                                {editingClient.client_type === 'organizational' && (
                                <>
                                    <h6 className="text-lg font-bold text-gray-800 mt-6 mb-4 md:col-span-2">Organizational Client Specifics</h6>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Organization Name:</label>
                                        <input type="text" name="organization_name" value={editingClient.organization_name || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Contact Person:</label>
                                        <input type="text" name="contact_person" value={editingClient.contact_person || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Registration Number:</label>
                                        <input type="text" name="registration_number" value={editingClient.registration_number || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Date of Incorporation:</label>
                                        <input type="date" name="date_of_incorporation" value={editingClient.date_of_incorporation || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Registered Address:</label>
                                        <input type="text" name="registered_address" value={editingClient.registered_address || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Physical Business Address:</label>
                                        <input type="text" name="physical_business_address" value={editingClient.physical_business_address || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Business Type:</label>
                                        <input type="text" name="business_type" value={editingClient.business_type || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Tax Identification Number (TIN):</label>
                                        <input type="text" name="tax_identification_number" value={editingClient.tax_identification_number || ''} onChange={handleEditClientChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                    </div>

                                    {/* Beneficial Owners Editing */}
                                    <div className="md:col-span-2 p-4 border rounded-md">
                                        <h5 className="text-lg font-bold text-gray-800 mb-4">Beneficial Owners</h5>
                                        {(editingClient.beneficial_owners && editingClient.beneficial_owners.length > 0) ? (
                                            editingClient.beneficial_owners.map((ubo, index) => (
                                                <div key={ubo.id || `new-${index}`} className="p-4 border rounded-md mb-4 space-y-4">
                                                    <div className="flex justify-between items-center">
                                                        <h6 className="font-semibold text-gray-700">Beneficial Owner {index + 1}</h6>
                                                        <button type="button" onClick={() => handleRemoveUbo(index, editingClient, setEditingClient)} className="text-red-500 hover:text-red-700">Remove</button>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <input type="text" name="first_name" value={ubo.first_name || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="First Name *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <input type="text" name="last_name" value={ubo.last_name || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Last Name *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <input type="email" name="email" value={ubo.email || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Email" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                                        <input type="text" name="phone_number" value={ubo.phone_number || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Phone Number" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                                        <div className="md:col-span-2">
                                                            <input type="text" name="physical_residential_address" value={ubo.physical_residential_address || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Physical Residential Address *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        </div>
                                                        <input type="text" name="id_or_passport_number" value={ubo.id_or_passport_number || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="ID/Passport Number *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <input type="text" name="id_type" value={ubo.id_type || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="ID Type *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <input type="text" name="country_of_issue" value={ubo.country_of_issue || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Country of Issue *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <input type="date" name="id_expiry_date" value={ubo.id_expiry_date || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="ID Expiry Date" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                                        <input type="date" name="date_of_birth" value={ubo.date_of_birth || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Date of Birth *" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                                        <div className="flex items-center">
                                                            <input type="checkbox" name="is_director" checked={ubo.is_director} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id={`editUboDirector-${index}`} />
                                                            <label htmlFor={`editUboDirector-${index}`} className="ml-2 block text-sm text-gray-900">Is Director?</label>
                                                        </div>
                                                        <div className="flex items-center">
                                                            <input type="checkbox" name="is_shareholder" checked={ubo.is_shareholder} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" id={`editUboShareholder-${index}`} />
                                                            <label htmlFor={`editUboShareholder-${index}`} className="ml-2 block text-sm text-gray-900">Is Shareholder?</label>
                                                        </div>
                                                        {ubo.is_shareholder && (
                                                            <input type="number" step="0.01" name="percentage_of_shares" value={ubo.percentage_of_shares || ''} onChange={e => handleUboChange(index, e, editingClient, setEditingClient)} placeholder="Percentage of Shares" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                                                        )}
                                                    </div>
                                                </div>
                                            )) 
                                        ) : (<p>No beneficial owners added yet.</p>)}
                                        <button type="button" onClick={() => handleAddUbo(editingClient, setEditingClient)} className="mt-4 bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                            + Add Another Beneficial Owner
                                        </button>
                                    </div>

                                    {/* Trust / Partnership Documents */}
                                    <div className="md:col-span-2 p-4 border rounded-md">
                                        <h5 className="text-lg font-bold text-gray-800 mb-4">Trust / Partnership Documents</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Trust Deed Document (current):</label>
                                                {editingClient.trust_deed_document_url ? (
                                                    <a href={editingClient.trust_deed_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                                ) : (
                                                    <span className="text-gray-500">No document uploaded</span>
                                                )}
                                                <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Trust Deed:</label>
                                                <input type="file" name="trust_deed_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Partnership Agreement Document (current):</label>
                                                {editingClient.partnership_agreement_document_url ? (
                                                    <a href={editingClient.partnership_agreement_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                                ) : (
                                                    <span className="text-gray-500">No document uploaded</span>
                                                )}
                                                <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Partnership Agreement:</label>
                                                <input type="file" name="partnership_agreement_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Regulatory / Key Documents */}
                                    <div className="md:col-span-2 p-4 border rounded-md">
                                        <h5 className="text-lg font-bold text-gray-800 mb-4">Regulatory / Key Documents</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">CIPA Registration (current):</label>
                                                {editingClient.cipa_registration_document_url ? (
                                                    <a href={editingClient.cipa_registration_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                                ) : (
                                                    <span className="text-gray-500">No document uploaded</span>
                                                )}
                                                <label className="block text-sm font-medium text-gray-700 mt-2">Upload New CIPA Registration:</label>
                                                <input type="file" name="cipa_registration_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Tax Clearance (current):</label>
                                                {editingClient.tax_clearance_document_url ? (
                                                    <a href={editingClient.tax_clearance_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                                ) : (
                                                    <span className="text-gray-500">No document uploaded</span>
                                                )}
                                                <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Tax Clearance:</label>
                                                <input type="file" name="tax_clearance_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Consent Form (current):</label>
                                                {editingClient.consent_form_document_url ? (
                                                    <a href={editingClient.consent_form_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900">View Document</a>
                                                ) : (
                                                    <span className="text-gray-500">No document uploaded</span>
                                                )}
                                                <label className="block text-sm font-medium text-gray-700 mt-2">Upload New Consent Form:</label>
                                                <input type="file" name="consent_form_document" onChange={handleEditClientChange} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Parties Involved (Comma-separated names for conflict check):</label>
                                        <textarea
                                            name="parties_involved"
                                            value={editingClient.parties_involved || ''}
                                            onChange={handleEditClientChange}
                                            className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            rows="2"
                                            placeholder="e.g., Opposing Party Name, Related Entity, Witness"
                                        ></textarea>
                                        <p className="mt-2 text-sm text-gray-500">Separate multiple names with commas.</p>
                                    </div>
                                </>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                            <button type="button" onClick={closeEditClientModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                            <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Save Changes</button>
                        </div>
                    </form>
                </div>
            </div>
        )}

      {/* Client Details Modal - Holistic View */}
      {showClientDetailsModal && selectedClient && (
        <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 h-full w-full z-50 transition-opacity duration-300 ${showClientDetailsModal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <div className={`absolute top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${showClientDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 sticky top-0 z-10">
                    <h5 className="text-2xl font-bold text-gray-800">
                        Client Details: {selectedClient.client_type === 'individual' ? `${selectedClient.first_name} ${selectedClient.last_name}` : selectedClient.organization_name}
                    </h5>
                    <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={openStatementModal}
                          className="bg-blue-600 text-white px-3 py-2 rounded-md shadow-sm hover:bg-blue-700 text-sm"
                        >
                          Generate Statement
                        </button>
                        <button onClick={closeClientDetailsModal} className="text-gray-600 hover:text-gray-900">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - 140px)' }}>
                    {isFetchingRecords ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
                            <p className="ml-4">Loading client records...</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="p-4 border rounded-md bg-gray-50">
                                <h6 className="text-lg font-bold text-gray-800 mb-4">Client Overview</h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div><strong>Risk Level:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${getStatusColor(selectedClient.risk_level)}-100 text-${getStatusColor(selectedClient.risk_level)}-800`}>{selectedClient.risk_level}</span></div>
                                    <div><strong>Client Portal Active:</strong> {selectedClient.is_portal_active ? 'Yes' : 'No'}</div>
                                    {selectedClient.invite_code && (
                                        <div className="flex items-center gap-2">
                                            <strong>Invite Code:</strong>
                                            <code className="bg-gray-200 px-2 py-0.5 rounded text-sm font-mono">{selectedClient.invite_code}</code>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(selectedClient.invite_code);
                                                    if (typeof showFormMessage === 'function') showFormMessage('success', 'Invite code copied!');
                                                }}
                                                className="text-blue-600 hover:text-blue-800 text-xs underline"
                                            >Copy</button>
                                        </div>
                                    )}
                                    
                                    {selectedClient.client_type === 'individual' && (
                                    <>
                                        <div><strong>Email:</strong> {selectedClient.email || 'N/A'}</div>
                                        <div><strong>Phone:</strong> {selectedClient.phone_number || 'N/A'}</div>
                                        <div><strong>Date of Birth:</strong> {selectedClient.date_of_birth ? new Date(selectedClient.date_of_birth).toLocaleDateString() : 'N/A'}</div>
                                        <div><strong>Gender:</strong> {selectedClient.gender || 'N/A'}</div>
                                        <div><strong>ID/Passport Number:</strong> {selectedClient.id_or_passport_number || 'N/A'}</div>
                                        <div><strong>ID Type:</strong> {selectedClient.id_type || 'N/A'}</div>
                                        <div><strong>Country of Issue:</strong> {selectedClient.country_of_issue || 'N/A'}</div>
                                        <div><strong>ID Expiry Date:</strong> {selectedClient.id_expiry_date ? new Date(selectedClient.id_expiry_date).toLocaleDateString() : 'N/A'}</div>
                                        <div><strong>Nationality:</strong> {selectedClient.nationality || 'N/A'}</div>
                                        <div><strong>Occupation:</strong> {selectedClient.occupation || 'N/A'}</div>
                                        <div>
                                            <strong>Proof of Address:</strong>
                                            {selectedClient.proof_of_address_document ? (
                                                <a href={selectedClient.proof_of_address_document} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <div>
                                            <strong>Consent Form:</strong>
                                            {selectedClient.consent_form_document_url ? (
                                                <a href={selectedClient.consent_form_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <div className="col-span-2"><strong>Physical Residential Address:</strong> {selectedClient.physical_residential_address || 'N/A'}</div>
                                    </>
                                    )}
                                    {selectedClient.client_type === 'organizational' && (
                                    <>
                                        <div><strong>Contact Person:</strong> {selectedClient.contact_person || 'N/A'}</div>
                                        <div><strong>Phone Number:</strong> {selectedClient.phone_number || 'N/A'}</div>
                                        <div><strong>Email:</strong> {selectedClient.email || 'N/A'}</div>
                                        <div><strong>Registration Number:</strong> {selectedClient.registration_number || 'N/A'}</div>
                                        <div><strong>Date of Incorporation:</strong> {selectedClient.date_of_incorporation ? new Date(selectedClient.date_of_incorporation).toLocaleDateString() : 'N/A'}</div>
                                        <div><strong>Business Type:</strong> {selectedClient.business_type || 'N/A'}</div>
                                        <div><strong>Tax Identification Number (TIN):</strong> {selectedClient.tax_identification_number || 'N/A'}</div>
                                        <div className="col-span-2"><strong>Registered Address:</strong> {selectedClient.registered_address || 'N/A'}</div>
                                        <div className="col-span-2"><strong>Physical Business Address:</strong> {selectedClient.physical_business_address || 'N/A'}</div>
                                        {Array.isArray(selectedClient.beneficial_owners) && selectedClient.beneficial_owners.length > 0 ? (
                                            <>
                                                <h6 className="text-lg font-bold text-gray-800 mt-4 mb-2 col-span-2">Beneficial Owners Details</h6>
                                                {selectedClient.beneficial_owners.map((ubo, uboIndex) => (
                                                    <div key={ubo.id || uboIndex} className="col-span-2 p-4 border rounded-md bg-white">
                                                        <h7 className="font-semibold text-gray-700 mb-2 block">Owner #{uboIndex + 1}</h7>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                            <div><strong>Name:</strong> {ubo.first_name || 'N/A'} {ubo.last_name || 'N/A'}</div>
                                                            <div><strong>Email:</strong> {ubo.email || 'N/A'}</div>
                                                            <div><strong>Phone:</strong> {ubo.phone_number || 'N/A'}</div>
                                                            <div className="col-span-2"><strong>Address:</strong> {ubo.physical_residential_address || 'N/A'}</div>
                                                            <div><strong>ID/Passport No.:</strong> {ubo.id_or_passport_number || 'N/A'}</div>
                                                            <div><strong>ID Type:</strong> {ubo.id_type || 'N/A'}</div>
                                                            <div><strong>Country of Issue:</strong> {ubo.country_of_issue || 'N/A'}</div>
                                                            <div><strong>ID Expiry Date:</strong> {ubo.id_expiry_date ? new Date(ubo.id_expiry_date).toLocaleDateString() : 'N/A'}</div>
                                                            <div><strong>Date of Birth:</strong> {ubo.date_of_birth ? new Date(ubo.date_of_birth).toLocaleDateString() : 'N/A'}</div>
                                                            <div><strong>Nationality:</strong> {ubo.nationality || 'N/A'}</div>
                                                            <div><strong>Is Director:</strong> {ubo.is_director ? 'Yes' : 'No'}</div>
                                                            <div><strong>Is Shareholder:</strong> {ubo.is_shareholder ? 'Yes' : 'No'}</div>
                                                            {ubo.is_shareholder && (
                                                                <div><strong>Percentage of Shares:</strong> {ubo.percentage_of_shares || 'N/A'}%</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <p className="col-span-2 text-gray-500">No beneficial owners recorded for this organization.</p>
                                        )}
                                        <h6 className="text-lg font-bold text-gray-800 mt-4 mb-2 col-span-2">Trust / Partnership Documents</h6>
                                        <div>
                                            <strong>Trust Deed Document:</strong>
                                            {selectedClient.trust_deed_document_url ? (
                                                <a href={selectedClient.trust_deed_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <div>
                                            <strong>Partnership Agreement Document:</strong>
                                            {selectedClient.partnership_agreement_document_url ? (
                                                <a href={selectedClient.partnership_agreement_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <h6 className="text-lg font-bold text-gray-800 mt-4 mb-2 col-span-2">Regulatory / Key Documents</h6>
                                        <div>
                                            <strong>CIPA Registration:</strong>
                                            {selectedClient.cipa_registration_document_url ? (
                                                <a href={selectedClient.cipa_registration_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <div>
                                            <strong>Tax Clearance:</strong>
                                            {selectedClient.tax_clearance_document_url ? (
                                                <a href={selectedClient.tax_clearance_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                        <div>
                                            <strong>Consent Form:</strong>
                                            {selectedClient.consent_form_document_url ? (
                                                <a href={selectedClient.consent_form_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900"> View Document</a>
                                            ) : ' N/A'}
                                        </div>
                                    </>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 border rounded-md bg-gray-50">
                                <h6 className="text-lg font-bold text-gray-800 mb-2">Parties Involved for Conflict Check</h6>
                                <p>{(Array.isArray(selectedClient.parties_involved) ? selectedClient.parties_involved.join(', ') : selectedClient.parties_involved) || 'N/A'}</p>
                            </div>

                            {/* Onboarding Checklist */}
                            <div className="p-4 border rounded-md bg-gray-50">
                                <h6 className="text-lg font-bold text-gray-800 mb-4">On-boarding Checklist</h6>
                                <div className="space-y-2">
                                    {selectedClient.onboarding_checklist && Object.entries(selectedClient.onboarding_checklist).map(([key, status]) => (
                                        <div key={key} className="flex justify-between items-center p-2 border-b">
                                            <span>{formatChecklistItemName(key)}</span>
                                            <div className="flex items-center space-x-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${getStatusColor(status)}-100 text-${getStatusColor(status)}-800`}>{formatChecklistItemName(status)}</span>
                                                <div className="relative inline-block text-left" ref={openChecklistDropdown === key ? dropdownRef : null}>
                                                    <button 
                                                        type="button" 
                                                        className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        onClick={() => setOpenChecklistDropdown(openChecklistDropdown === key ? null : key)}
                                                    >
                                                        Update
                                                        <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                    {openChecklistDropdown === key && (
                                                      <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                                                          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                                                              <button onClick={() => handleUpdateOnboardingStatus(key, 'pending')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Pending</button>
                                                              <button onClick={() => handleUpdateOnboardingStatus(key, 'in_progress')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">In Progress</button>
                                                              <button onClick={() => handleUpdateOnboardingStatus(key, 'completed')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Completed</button>
                                                              <button onClick={() => handleUpdateOnboardingStatus(key, 'skipped')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Skipped</button>
                                                          </div>
                                                      </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                             {/* Tabs for Client Records */}
                            <div className="border rounded-md">
                                <div className="border-b border-gray-200">
                                    <ul className="flex flex-wrap -mb-px text-sm font-medium text-center text-gray-500">
                                        <li className="mr-2">
                                            <button onClick={() => setActiveDetailsTab('cases')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'cases' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Cases ({clientRecords.cases?.length || 0})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('documents')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'documents' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Documents ({clientRecords.documents?.length || 0})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('time_expenses')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'time_expenses' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Time & Expenses ({(clientRecords.timeEntries?.length || 0) + (clientRecords.expenseEntries?.length || 0)})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('billing')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'billing' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Billing ({clientRecords.invoices?.length || 0})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('debt_recovery')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'debt_recovery' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Debt Recovery ({clientRecords.debtRecoveryRequests?.length || 0})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('comms_log')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'comms_log' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Comms Log ({clientRecords.communicationLogs?.length || 0})
                                            </button>
                                        </li>
                                        <li className="mr-2">
                                             <button onClick={() => setActiveDetailsTab('tasks')} className={`inline-block p-4 rounded-t-lg border-b-2 ${activeDetailsTab === 'tasks' ? 'text-blue-600 border-blue-600' : 'border-transparent hover:text-gray-600 hover:border-gray-300'}`}>
                                                Tasks ({clientRecords.tasks?.length || 0})
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                                <div className="p-4">
                                  {/* Cases Tab Content */}
                                  {activeDetailsTab === 'cases' && (
                                      <div>
                                          {clientRecords.cases && clientRecords.cases.length > 0 ? (
                                              <table className="min-w-full divide-y divide-gray-200">
                                                  <thead className="bg-gray-50">
                                                      <tr>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Opened</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                      {clientRecords.cases.map(item => (
                                                      <tr key={item.id}>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.title || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status_display || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.case_type_display || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.case_stage_display || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.date_opened).toLocaleDateString()}</td>
                                                      </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          ) : (<p>No cases found for this client.</p>)}
                                      </div>
                                  )}

                                  {/* Documents Tab Content - real folder browser, one tree per matter */}
                                  {activeDetailsTab === 'documents' && (
                                    <div className="border border-gray-200 rounded-md overflow-hidden" style={{ height: '65vh' }}>
                                        <DocumentExplorer
                                            authAxios={authAxios}
                                            onToast={(msg, type) => triggerToast(msg, type === 'error' ? 'danger' : type)}
                                            mode="client"
                                            clientId={selectedClient.id}
                                            clientName={selectedClient.client_type === 'organizational' ? selectedClient.organization_name : `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim()}
                                        />
                                    </div>
                                  )}
                                  
                                  {/* Time & Expenses Tab Content */}
                                  {activeDetailsTab === 'time_expenses' && (
                                    <div>
                                      <h5 className="text-lg font-bold text-gray-800 mb-4">Time Entries</h5>
                                      {clientRecords.timeEntries && clientRecords.timeEntries.length > 0 ? (
                                          <table className="min-w-full divide-y divide-gray-200 mb-6">
                                              <thead className="bg-gray-50">
                                                  <tr>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                  </tr>
                                              </thead>
                                              <tbody className="bg-white divide-y divide-gray-200">
                                                  {clientRecords.timeEntries.map(item => (
                                                  <tr key={item.id}>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{parseFloat(item.duration_hours).toFixed(2)}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.is_billable ? 'Yes' : 'No'}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.start_time).toLocaleString()}</td>
                                                  </tr>
                                                  ))}
                                              </tbody>
                                          </table>
                                      ) : (<p>No time entries found for this client.</p>)}

                                      <h5 className="text-lg font-bold text-gray-800 mb-4">Disbursements</h5>
                                      {clientRecords.expenseEntries && clientRecords.expenseEntries.length > 0 ? (
                                          <table className="min-w-full divide-y divide-gray-200">
                                              <thead className="bg-gray-50">
                                                  <tr>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                  </tr>
                                              </thead>
                                              <tbody className="bg-white divide-y divide-gray-200">
                                                  {clientRecords.expenseEntries.map(item => (
                                                  <tr key={item.id}>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(item.amount)}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.category}</td>
                                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                                                  </tr>
                                                  ))}
                                              </tbody>
                                          </table>
                                      ) : (<p>No disbursements found for this client.</p>)}
                                    </div>
                                  )}
                                  
                                  {/* Billing Tab Content */}
                                  {activeDetailsTab === 'billing' && (
                                      <div>
                                          {clientRecords.invoices && clientRecords.invoices.length > 0 ? (
                                              <table className="min-w-full divide-y divide-gray-200">
                                                  <thead className="bg-gray-50">
                                                      <tr>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Due</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                      {clientRecords.invoices.map(item => (
                                                      <tr key={item.id}>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.invoice_number}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(item.total_amount)}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(item.balance_due)}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.due_date).toLocaleDateString()}</td>
                                                      </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          ) : (<p>No invoices found for this client.</p>)}
                                      </div>
                                  )}

                                  {/* Debt Recovery Tab Content */}
                                  {activeDetailsTab === 'debt_recovery' && (
                                      <div>
                                          {clientRecords.debtRecoveryRequests && clientRecords.debtRecoveryRequests.length > 0 ? (
                                              <table className="min-w-full divide-y divide-gray-200">
                                                  <thead className="bg-gray-50">
                                                      <tr>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                      {clientRecords.debtRecoveryRequests.map(item => (
                                                      <tr key={item.id}>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.reference || `DR-${item.id}`}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.title || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status || 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(item.principal_amount || 0)}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(item.balance_remaining || 0)}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.assigned_to_username || '—'}</td>
                                                      </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          ) : (<p>No debt recovery requests found for this client.</p>)}
                                      </div>
                                  )}

                                  {/* Comms Log Tab Content */}
                                  {activeDetailsTab === 'comms_log' && (
                                      <div>
                                          {clientRecords.communicationLogs && clientRecords.communicationLogs.length > 0 ? (
                                              <table className="min-w-full divide-y divide-gray-200">
                                                  <thead className="bg-gray-50">
                                                      <tr>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Summary</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logged By</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                      {clientRecords.communicationLogs.map(item => (
                                                      <tr key={item.id}>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.log_type}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.subject}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(item.timestamp).toLocaleString()}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.logged_by_username || 'N/A'}</td>
                                                      </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          ) : (<p>No communication logs found for this client.</p>)}
                                      </div>
                                  )}

                                  {/* Tasks Tab Content */}
                                  {activeDetailsTab === 'tasks' && (
                                      <div>
                                          {clientRecords.tasks && clientRecords.tasks.length > 0 ? (
                                              <table className="min-w-full divide-y divide-gray-200">
                                                  <thead className="bg-gray-50">
                                                      <tr>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                                                      </tr>
                                                  </thead>
                                                  <tbody className="bg-white divide-y divide-gray-200">
                                                      {clientRecords.tasks.map(item => (
                                                      <tr key={item.id}>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.title}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status.replace('_', ' ')}</td>
                                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.priority}</td>
                                                      </tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          ) : (<p>No tasks found for this client.</p>)}
                                      </div>
                                  )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex justify-end space-x-4 p-4 border-t bg-gray-50 sticky bottom-0">
                    <button type="button" onClick={() => openEditClientModal(selectedClient)} className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                        Edit Client
                    </button>
                    <button type="button" onClick={closeClientDetailsModal} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Close</button>
                </div>
            </div>
        </div>
      )}

            {/* Statement Modal */}
            {showStatementModal && selectedClient && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="p-4 border-b flex items-center justify-between">
                            <h5 className="text-lg font-semibold text-gray-800">Generate Statement</h5>
                            <button
                                type="button"
                                className="text-gray-600 hover:text-gray-900"
                                onClick={() => setShowStatementModal(false)}
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            <div className="text-sm text-gray-600">
                                Client: <span className="font-medium text-gray-900">{selectedClient.client_type === 'individual' ? `${selectedClient.first_name} ${selectedClient.last_name}` : selectedClient.organization_name}</span>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button type="button" className={`px-3 py-1 rounded border text-sm ${statementPreset === '30' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`} onClick={() => applyStatementPreset('30')}>Last 30 Days</button>
                                <button type="button" className={`px-3 py-1 rounded border text-sm ${statementPreset === '90' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`} onClick={() => applyStatementPreset('90')}>Last 90 Days</button>
                                <button type="button" className={`px-3 py-1 rounded border text-sm ${statementPreset === '365' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`} onClick={() => applyStatementPreset('365')}>Last 12 Months</button>
                                <button type="button" className={`px-3 py-1 rounded border text-sm ${statementPreset === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`} onClick={() => applyStatementPreset('all')}>All Time</button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={statementStartDate}
                                        onChange={(e) => { setStatementPreset('custom'); setStatementStartDate(e.target.value); }}
                                        className="w-full border rounded-md px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={statementEndDate}
                                        onChange={(e) => { setStatementPreset('custom'); setStatementEndDate(e.target.value); }}
                                        className="w-full border rounded-md px-3 py-2"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="includeOutstanding"
                                    checked={includeOutstanding}
                                    onChange={(e) => setIncludeOutstanding(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="includeOutstanding" className="text-sm text-gray-700">Include Outstanding Invoices</label>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="includeOutstanding"
                                    checked={includeOutstanding}
                                    onChange={(e) => setIncludeOutstanding(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="includeOutstanding" className="text-sm text-gray-700">Include Outstanding Invoices</label>
                            </div>
                        </div>

                        <div className="p-4 border-t flex justify-end gap-3 bg-gray-50">
                            <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300" onClick={() => setShowStatementModal(false)}>Cancel</button>
                            {selectedClient && (
                                <SendPdfButton
                                    authAxios={authAxios}
                                    showFormMessage={sendPdfToast}
                                    fetchPdf={fetchClientStatementPdf}
                                    defaultFilename={`statement-client-${selectedClient.id}-${statementStartDate || 'all'}-to-${statementEndDate || 'today'}.pdf`}
                                    defaultSubject={`Statement of Account — ${selectedClient.first_name ? `${selectedClient.first_name} ${selectedClient.last_name}` : (selectedClient.organization_name || `Client #${selectedClient.id}`)}`}
                                    defaultBody={`Dear ${selectedClient.first_name || selectedClient.organization_name || 'Client'},\n\nPlease find attached your statement of account for the period ${statementStartDate || 'all time'} to ${statementEndDate || 'today'}.\n\nKind regards,`}
                                    defaultRecipients={selectedClient.email ? [selectedClient.email] : []}
                                    documentKind="CLIENT_STATEMENT"
                                    objectId={selectedClient.id}
                                    buttonLabel="Email Statement"
                                    buttonClassName="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                                />
                            )}
                            <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700" onClick={handleDownloadStatement}>Download PDF</button>
                        </div>
                    </div>
                </div>
            )}

      <ConflictCheckModal
        show={showConflictCheckModal}
        onClose={() => setShowConflictCheckModal(false)}
        conflictCheckResult={currentConflictCheckResult}
        authAxios={authAxios}
        onConflictResolved={handleConflictResolved}
        currentUser={currentUser}
      />

      {clientToArchive && (
        <div className="fixed inset-0 bg-gray-700 bg-opacity-60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900">Archive client?</h3>
            <p className="mt-3 text-sm text-gray-700">
              Are you sure you want to archive
              {' '}<strong>
                {clientToArchive.client_type === 'individual'
                  ? `${clientToArchive.first_name || ''} ${clientToArchive.last_name || ''}`.trim()
                  : (clientToArchive.organization_name || 'this client')}
              </strong>?
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Archived clients are hidden from the active list but their cases, invoices, and history are preserved. You can restore them later.
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeArchiveConfirm}
                disabled={archiveInProgress}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmArchiveClient}
                disabled={archiveInProgress}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
              >
                {archiveInProgress ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

            {/* Local toast UI removed in favor of global toasts (showFormMessage). */}
    </div>
  );
}

export default ClientManagement;